import { Device } from '@capacitor/device';
import { registerPlugin } from '@capacitor/core';

import { isNativePlatform } from './capacitor';

// Register the FirebaseMessaging plugin on native only
const FirebaseMessaging: any = isNativePlatform() ? registerPlugin('FirebaseMessaging') : null;

type PermissionStatus = 'granted' | 'denied' | 'prompt';

/**
 * Get the device platform
 */
export async function getPlatformName(): Promise<'ios' | 'android' | 'web'> {
  if (!isNativePlatform()) return 'web';
  const info = await Device.getInfo();
  return info.platform as 'ios' | 'android';
}

/**
 * Get a unique device identifier
 */
export async function getDeviceId(): Promise<string> {
  if (!isNativePlatform()) return `web_${Date.now()}`;
  const id = await Device.getId();
  return (id as any).identifier || (id as any).uuid || `device_${Date.now()}`;
}

/**
 * Check current notification permission status
 */
export async function checkPermissionStatus(): Promise<PermissionStatus> {
  if (!FirebaseMessaging) return 'denied';

  try {
    const result = await FirebaseMessaging.checkPermissions();
    return result.receive as PermissionStatus;
  } catch (error) {
    console.error('Error checking permission status:', error);
    return 'denied';
  }
}

/**
 * Request notification permissions
 */
export async function requestPermissions(): Promise<PermissionStatus> {
  if (!FirebaseMessaging) return 'denied';

  try {
    const result = await FirebaseMessaging.requestPermissions();
    return result.receive === 'granted' ? 'granted' : 'denied';
  } catch (error) {
    console.error('Error requesting permissions:', error);
    return 'denied';
  }
}

/**
 * Initialize FCM and get token
 */
export async function initializeFCM(): Promise<{
  token: string;
  platform: string;
  deviceId: string;
} | null> {
  if (!FirebaseMessaging) return null;

  try {
    const status = await checkPermissionStatus();
    if (status !== 'granted') return null;

    const [platform, deviceId, tokenResult] = await Promise.all([
      getPlatformName(),
      getDeviceId(),
      FirebaseMessaging.getToken(),
    ]);

    if (!tokenResult?.token) return null;

    return { token: tokenResult.token, platform, deviceId };
  } catch (error) {
    console.error('Error initializing FCM:', error);
    return null;
  }
}

/**
 * Add foreground notification listener
 */
export function addNotificationReceivedListener(callback: (notification: any) => void): {
  remove: () => void;
} {
  if (!FirebaseMessaging) return { remove: () => {} };

  const listener = FirebaseMessaging.addListener('notificationReceived', callback);
  return {
    remove: () => {
      if (listener && typeof listener.remove === 'function') {
        listener.remove();
      } else if (listener instanceof Promise) {
        listener.then((l: any) => l?.remove?.());
      }
    },
  };
}

/**
 * Add notification tap/action listener
 */
export function addNotificationActionListener(callback: (notification: any) => void): {
  remove: () => void;
} {
  if (!FirebaseMessaging) return { remove: () => {} };

  const listener = FirebaseMessaging.addListener('notificationActionPerformed', callback);
  return {
    remove: () => {
      if (listener && typeof listener.remove === 'function') {
        listener.remove();
      } else if (listener instanceof Promise) {
        listener.then((l: any) => l?.remove?.());
      }
    },
  };
}

/**
 * Add token refresh listener
 */
export function addTokenRefreshListener(callback: (event: { token: string }) => void): {
  remove: () => void;
} {
  if (!FirebaseMessaging) return { remove: () => {} };

  const listener = FirebaseMessaging.addListener('tokenReceived', callback);
  return {
    remove: () => {
      if (listener && typeof listener.remove === 'function') {
        listener.remove();
      } else if (listener instanceof Promise) {
        listener.then((l: any) => l?.remove?.());
      }
    },
  };
}

/**
 * Remove all FCM listeners
 */
export async function removeAllListeners(): Promise<void> {
  if (!FirebaseMessaging) return;
  try {
    await FirebaseMessaging.removeAllListeners();
  } catch (error) {
    console.error('Error removing listeners:', error);
  }
}

/**
 * Check if FCM is supported on this platform
 */
export function isFCMSupported(): boolean {
  return isNativePlatform() && !!FirebaseMessaging;
}
